import React, { forwardRef } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
  iconRight?: React.ReactNode;
  className?: string;
}

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '#E5E8F1',
  borderRadius: '10px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#001A70',
  outline: 'none',
  background: 'white',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '10px',
  fontWeight: 800,
  color: '#9CA3AF',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginBottom: '5px',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, helperText, error, iconRight, className = '', style, onFocus, onBlur, id, ...props }, ref) => {
    const hasError = Boolean(error);
    const [isFocused, setIsFocused] = React.useState(false);
    const inputId = id || React.useId();

    const combinedStyle: React.CSSProperties = {
      ...inputBaseStyle,
      ...(props.disabled ? { backgroundColor: '#F9FAFB', color: '#9CA3AF', cursor: 'not-allowed' } : {}),
      ...(isFocused ? { borderColor: '#0063EA', boxShadow: '0 0 0 3px rgba(0,99,234,0.1)' } : {}),
      ...(iconRight ? { paddingRight: '2.5rem' } : {}),
      ...style,
    };

    return (
      <div className={className} style={{ display: 'flex', flexDirection: 'column' }}>
        {label && (
          <label htmlFor={inputId} style={labelStyle}>
            {label}
          </label>
        )}

        <div style={{ position: 'relative' }}>
          <input
            ref={ref}
            id={inputId}
            style={combinedStyle}
            onFocus={(e) => { setIsFocused(true); onFocus?.(e); }}
            onBlur={(e) => { setIsFocused(false); onBlur?.(e); }}
            {...props}
          />
          {iconRight && (
            <span style={{
              position: 'absolute', right: '10px', top: '50%',
              transform: 'translateY(-50%)', color: '#9ca3af',
              display: 'flex', alignItems: 'center',
            }}>
              {iconRight}
            </span>
          )}
        </div>

        {helperText && !hasError && (
          <p style={{ fontSize: '11px', color: '#6b7280', margin: '4px 0 0' }}>{helperText}</p>
        )}
        {hasError && (
          <p style={{ fontSize: '11px', color: '#ef4444', margin: '4px 0 0' }}>{error}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
